import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Play, Pause, SkipForward, X, Timer, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, BookOpen, Info, Sparkles,
} from 'lucide-react';

// Renderer for individual step content — same logic as SalleSeancePage but lighter
function StepRenderer({ step }) {
  if (step.type === 'article') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Article magazine</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Lecture commentée de l'article. Projeter et discuter point par point.</p>
        <Link to={`/magazine/${step.ref}`} target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px] hover:bg-white/[0.1]">
          Ouvrir l'article · /magazine/{step.ref}
        </Link>
      </div>
    );
  }
  if (step.type === 'simulator') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Simulateur interactif</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Faire manipuler les sliders devant la classe — les élèves observent l'impact en direct.</p>
        <Link to={step.ref || '/simulateurs'} target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold">
          Ouvrir le simulateur
        </Link>
      </div>
    );
  }
  if (step.type === 'data') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Données officielles ONISR</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Présenter le baromètre ONISR le plus récent — chiffres clés du mois en cours.</p>
        <Link to="/onisr" target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px]">
          Ouvrir le baromètre ONISR
        </Link>
      </div>
    );
  }
  if (step.type === 'discussion') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Échange dirigé</div>
        {step.questions?.length ? (
          <ul className="space-y-3 text-white/90 text-[20px] leading-relaxed">
            {step.questions.map((q, i) => (<li key={i} className="flex gap-3"><span className="text-red-500 font-serif-editorial text-[24px] shrink-0">{i + 1}.</span>{q}</li>))}
          </ul>
        ) : (
          <p className="text-white/75 text-[19px] leading-relaxed">Discussion ouverte avec l'élève et / ou ses représentants légaux sur le thème de cette étape.</p>
        )}
      </div>
    );
  }
  if (step.type === 'progress') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Bilan de progression</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-3">Présenter la progression REMC de l'élève. En mode aperçu, ouvrez le livret d'un élève réel dans un autre onglet.</p>
        <Link to="/enseignant/dashboard" target="_blank" rel="noopener" className="text-red-400 hover:text-red-300 text-[13.5px]">
          Ouvrir le tableau de bord enseignant →
        </Link>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Synthèse</div>
      <p className="text-white/75 text-[19px] leading-relaxed">Reformuler les points clés avec l'élève · Engagement pour la prochaine séance.</p>
    </div>
  );
}

// ===================================================================
// SallePreviewPage : mode aperçu d'un cours en salle SANS élève lié.
// Permet à l'enseignant de consulter / projeter / présenter aux parents
// sans avoir à formaliser une séance dans le livret d'un élève.
// Aucune donnée n'est persistée — pas de PDF, pas de notes sauvegardées.
// ===================================================================
export default function SallePreviewPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'enseignant') { navigate('/'); return; }
    api.seanceTemplate(templateId)
      .then(setTpl)
      .catch(() => setTpl({ error: 'Modèle introuvable' }));
  }, [loading, user, templateId, navigate]);

  useEffect(() => {
    if (!running) { if (tickRef.current) clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === 'TEXTAREA' || e.target?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); setStepIdx((i) => Math.min((tpl?.steps?.length || 1) - 1, i + 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setStepIdx((i) => Math.max(0, i - 1)); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tpl]);

  if (loading || !tpl) return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (tpl.error) return <div className="min-h-screen bg-black text-white flex items-center justify-center gap-3"><AlertCircle /> {tpl.error}</div>;

  const steps = tpl.steps || [];
  const step = steps[stepIdx];
  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0a0a0b]/95 backdrop-blur px-4 lg:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="preview-close" onClick={() => navigate('/enseignant/seances')} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/[0.1]" aria-label="Quitter">
              <X size={15} />
            </button>
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold truncate flex items-center gap-2">
                <Sparkles size={11} /> Mode aperçu · {tpl.category}
              </div>
              <h1 className="font-serif-editorial text-white text-[18px] lg:text-[22px] leading-tight truncate">{tpl.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 h-9 rounded-full bg-white/[0.06] border border-white/15 text-white font-mono-spec text-[13px]">
              <Timer size={14} className={running ? 'text-red-400' : 'text-white/50'} /> {mmss(elapsed)}
            </div>
            {!running ? (
              <button data-testid="preview-start" onClick={() => setRunning(true)} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold">
                <Play size={13} /> Démarrer chrono
              </button>
            ) : (
              <button onClick={() => setRunning(false)} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px]"><Pause size={13} /> Pause</button>
            )}
          </div>
        </div>
      </header>

      {/* Bandeau d'info aperçu */}
      <div className="bg-yellow-950/30 border-b border-yellow-900/40 px-4 lg:px-6 py-2.5 text-yellow-200/90 text-[12.5px] flex items-center gap-2 justify-center">
        <Info size={13} className="shrink-0" />
        <span>Mode aperçu — aucune donnée n'est enregistrée. Pour générer un compte-rendu, créez une séance liée à un élève.</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 grid lg:grid-cols-[1fr_300px] gap-6">
        {/* Slide */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d0e] to-[#080809] p-8 lg:p-12 min-h-[60vh] flex flex-col">
          <div className="flex items-center justify-between mb-7">
            <div className="text-[12px] uppercase tracking-[0.2em] text-white/40 font-mono-spec">Étape {stepIdx + 1} / {steps.length}</div>
            <div className="text-[12px] text-white/40 font-mono-spec">Durée totale prévue · {tpl.duration_min} min</div>
          </div>

          <h2 data-testid="preview-step-title" className="font-serif-editorial text-white text-[40px] lg:text-[56px] leading-[1.05] tracking-tight mb-3">{step?.title}</h2>
          {step?.duration > 0 && <div className="text-white/45 text-[13px] font-mono-spec mb-7">⏱ {step.duration} min prévues</div>}

          <div className="mt-2 flex-1">
            {step && <StepRenderer step={step} />}
          </div>

          <div className="mt-10 flex items-center justify-between gap-3 pt-6 border-t border-white/5">
            <button data-testid="preview-prev" onClick={() => setStepIdx((i) => Math.max(0, i - 1))} disabled={stepIdx === 0} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/[0.04] border border-white/10 text-white text-[13.5px] hover:bg-white/[0.08] disabled:opacity-40">
              <ChevronLeft size={15} /> Précédent
            </button>
            <div className="text-[11px] text-white/40 font-mono-spec hidden lg:block">← → ou Espace pour naviguer</div>
            {stepIdx < steps.length - 1 ? (
              <button data-testid="preview-next" onClick={() => setStepIdx((i) => i + 1)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold">
                Étape suivante <SkipForward size={15} />
              </button>
            ) : (
              <button onClick={() => navigate('/enseignant/seances')} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/[0.06] border border-white/15 text-white text-[13.5px] font-semibold">
                <ChevronRight size={15} /> Retour à la bibliothèque
              </button>
            )}
          </div>
        </div>

        {/* Sidebar étapes */}
        <aside>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:sticky lg:top-24">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Déroulé · {tpl.duration_min} min</div>
            <ol className="space-y-2">
              {steps.map((st, i) => {
                const active = i === stepIdx;
                return (
                  <li key={i}>
                    <button data-testid={`preview-step-${i}`} onClick={() => setStepIdx(i)} className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left text-[12.5px] transition border ${active ? 'border-red-500 bg-red-950/30 text-white' : 'border-white/8 bg-white/[0.02] text-white/65 hover:bg-white/[0.05]'}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${active ? 'bg-red-600 text-white' : 'bg-white/10 text-white/70'}`}>{i + 1}</span>
                      <span className="flex-1 truncate">{st.title}</span>
                      <span className="text-white/35 text-[10px] font-mono-spec">{st.duration}m</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-5 pt-5 border-t border-white/5 text-[11.5px] text-white/55 leading-relaxed">
              <BookOpen size={13} className="inline -mt-0.5 mr-1 text-red-400" />
              Pour générer un compte-rendu PDF signé par les parents et l'élève, créez une séance officielle depuis le bouton "Nouvelle séance" lié à un élève.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
